import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1758518726869-01aff69a56e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMHBlb3BsZXxlbnwwfHx8fDE3NzgyODAzNTB8MA&ixlib=rb-4.1.0&q=85";

const VALUE_ICONS = [ShieldCheck, Sparkles, CheckCircle2];

export default function About() {
  const { t } = useT();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);

  return (
    <section
      id="about"
      ref={ref}
      data-testid="about-section"
      className="relative bg-[#F8F9FA] py-28 lg:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section Label */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="overline">{t.about.overline}</span>
          <div className="h-px w-16 bg-slate-300" />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Headline */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.025em] leading-[1.02] text-slate-900">
              {t.about.title1}<br />
              <span className="text-slate-400">{t.about.title2}</span>
            </h2>
            <p className="mt-8 text-lg leading-relaxed text-slate-600 max-w-2xl">
              {t.about.body}
            </p>

            {/* Values list */}
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {t.about.values.map((v, i) => {
                const Icon = VALUE_ICONS[i];
                return (
                  <motion.div
                    key={v.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.1 * i }}
                    className="group"
                    data-testid={`about-value-${i}`}
                  >
                    <div className="w-11 h-11 rounded-lg bg-[#005BEA]/10 flex items-center justify-center mb-4 group-hover:bg-[#005BEA] transition-colors">
                      <Icon className="w-5 h-5 text-[#005BEA] group-hover:text-white transition-colors" strokeWidth={2.2} />
                    </div>
                    <h3 className="font-display font-semibold text-slate-900 text-base mb-2">
                      {v.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {v.desc}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Parallax image card */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-slate-200 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25)]">
              <motion.img
                style={{ y: imgY }}
                src={ABOUT_IMG}
                alt={t.about.title1}
                className="w-full h-[130%] object-cover will-change-transform"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
            </div>

            {/* Floating stat */}
            <div
              data-testid="about-stat-card"
              className="absolute -bottom-8 -left-8 lg:-left-16 bg-white border border-slate-200 rounded-xl p-6 shadow-[0_18px_50px_-15px_rgba(15,23,42,0.18)] max-w-[260px]"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-2 h-2 rounded-full bg-[#FF6B00] animate-pulse" />
                <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-slate-500">
                  {t.about.statLabel}
                </span>
              </div>
              <div className="font-display text-4xl font-bold tracking-tight text-slate-900 mb-1">
                {t.about.statNum}
              </div>
              <p className="text-sm text-slate-600 leading-snug">
                {t.about.statBody}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
